import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../utils/tailwindUtils";
import { Building2, Check, ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export const PENALTY_MODE_OPTIONS = [
    { value: "fine", label: "Fine" },
    { value: "deduction", label: "Deduction" },
];

export const SettingCard = ({ icon: Icon, title, description, children, className }) => (
    <div className={cn(
        "bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5",
        className
    )}>
        <div className="flex items-start gap-2.5 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            {Icon && (
                <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                    <Icon className="h-4.5 w-4.5" />
                </span>
            )}
            <div className="min-w-0">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
                {description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
                )}
            </div>
        </div>
        {children}
    </div>
);

export const Toggle = ({ checked, onChange, disabled }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
            "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-sidebar-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800",
            checked ? "bg-sidebar" : "bg-gray-300 dark:bg-gray-600",
            disabled && "opacity-50 cursor-not-allowed"
        )}
    >
        <span
            className={cn(
                "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
                checked ? "translate-x-[22px]" : "translate-x-0.5"
            )}
        />
    </button>
);

export const ToggleRow = ({ label, description, configKey, checked, onChange, disabled }) => (
    <div className="flex items-center justify-between gap-4 py-2.5">
        <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-800 dark:text-gray-200">{label}</label>
            {configKey && (
                <p className="text-[11px] font-mono text-gray-400 dark:text-gray-500 mt-0.5">{configKey}</p>
            )}
            {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
        </div>
        <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
);

export const NumberField = ({ label, hint, prefix, suffix, value, onChange, min, max, step }) => (
    <div>
        <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5">{label}</label>
        <div className="relative">
            {prefix && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                    {prefix}
                </span>
            )}
            <input
                type="number"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={onChange}
                className={cn(
                    "w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900",
                    "px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100",
                    "focus:outline-none focus:ring-2 focus:ring-sidebar-primary focus:border-sidebar-primary transition",
                    prefix && "pl-7",
                    suffix && "pr-12"
                )}
            />
            {suffix && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                    {suffix}
                </span>
            )}
        </div>
        {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</p>}
    </div>
);

export const SelectField = ({ label, hint, value, onChange, options, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const selected = options.find((opt) => String(opt.value) === String(value));
    const selectedLabel = selected?.label ?? "Select…";

    useEffect(() => {
        const onDocClick = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const handleSelect = (optValue) => {
        onChange({ target: { value: optValue } });
        setOpen(false);
    };

    return (
        <div ref={ref} className="relative">
            <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5">{label}</label>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((prev) => !prev)}
                className={cn(
                    "w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm text-left transition",
                    "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100",
                    "border-[#0BB5AC]/40 hover:border-[#0BB5AC]/60 hover:bg-[#0BB5AC]/5",
                    "focus:outline-none focus:ring-2 focus:ring-[#0BB5AC]/30 focus:border-[#0BB5AC]",
                    open && "ring-2 ring-[#0BB5AC]/30 border-[#0BB5AC]",
                    disabled && "cursor-not-allowed opacity-70"
                )}
            >
                <span className="truncate">{selectedLabel}</span>
                <ChevronDown className={cn("h-4 w-4 shrink-0 text-[#0BB5AC] transition-transform", open && "rotate-180")} />
            </button>

            {open && !disabled && (
                <div
                    className={cn(
                        "absolute left-0 right-0 z-50 mt-1 rounded-lg border border-[#0BB5AC]/20",
                        "bg-white dark:bg-gray-900 shadow-lg overflow-hidden"
                    )}
                >
                    <div className="max-h-56 overflow-y-auto py-1">
                        {options.map((opt) => {
                            const isActive = String(opt.value) === String(value);
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => handleSelect(opt.value)}
                                    className={cn(
                                        "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                                        isActive
                                            ? "bg-[#0BB5AC]/10 text-[#0BB5AC] font-medium"
                                            : "text-gray-900 dark:text-gray-100 hover:bg-[#0BB5AC]/5 hover:text-[#0BB5AC]"
                                    )}
                                >
                                    <span className="flex-1">{opt.label}</span>
                                    {isActive && <Check className="h-4 w-4 shrink-0 text-[#0BB5AC]" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</p>}
        </div>
    );
};

export const CompactSelect = ({ value, onChange, options, disabled = false, className }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const [menuStyle, setMenuStyle] = useState({});

    const selected = options.find((opt) => String(opt.value) === String(value));
    const selectedLabel = selected?.label ?? "Select…";

    useEffect(() => {
        const onDocClick = (event) => {
            if (ref.current?.contains(event.target)) return;
            if (event.target.closest?.("[data-compact-select-menu]")) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    useEffect(() => {
        if (!open || !ref.current) return;

        const updatePosition = () => {
            const rect = ref.current.getBoundingClientRect();
            setMenuStyle({
                position: "fixed",
                top: rect.bottom + 4,
                left: rect.left,
                minWidth: Math.max(rect.width, 120),
                zIndex: 9999,
            });
        };

        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [open]);

    const handleSelect = (optValue) => {
        onChange(optValue);
        setOpen(false);
    };

    const menu =
        open && !disabled
            ? createPortal(
                <div
                    style={menuStyle}
                    data-compact-select-menu
                    className="rounded-lg border border-[#0BB5AC]/20 bg-white dark:bg-gray-900 shadow-lg py-1"
                >
                    {options.map((opt) => {
                        const isActive = String(opt.value) === String(value);
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => handleSelect(opt.value)}
                                className={cn(
                                    "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                                    isActive
                                        ? "bg-[#0BB5AC]/10 text-[#0BB5AC] font-medium"
                                        : "text-gray-900 dark:text-gray-100 hover:bg-[#0BB5AC]/5 hover:text-[#0BB5AC]"
                                )}
                            >
                                <span className="flex-1">{opt.label}</span>
                                {isActive && <Check className="h-4 w-4 shrink-0 text-[#0BB5AC]" />}
                            </button>
                        );
                    })}
                </div>,
                document.body
            )
            : null;

    return (
        <div ref={ref} className={cn("relative", className)}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((prev) => !prev)}
                className={cn(
                    "min-w-[110px] flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-sm text-left transition",
                    "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100",
                    "border-[#0BB5AC]/40 hover:border-[#0BB5AC]/60 hover:bg-[#0BB5AC]/5",
                    "focus:outline-none focus:ring-2 focus:ring-[#0BB5AC]/30 focus:border-[#0BB5AC]",
                    open && "ring-2 ring-[#0BB5AC]/30 border-[#0BB5AC]",
                    disabled && "cursor-not-allowed opacity-70"
                )}
            >
                <span className="truncate">{selectedLabel}</span>
                <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-[#0BB5AC] transition-transform", open && "rotate-180")} />
            </button>
            {menu}
        </div>
    );
};

export const LinkCard = ({ icon: Icon, title, description, href }) => (
    <Link
        to={href}
        className="group flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 hover:border-sidebar-primary/40 hover:bg-sidebar/5 transition-colors"
    >
        <span className="w-9 h-9 flex-shrink-0 rounded-lg bg-sidebar/10 text-sidebar-primary grid place-items-center">
            <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</p>
            {description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{description}</p>
            )}
        </div>
        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-sidebar-primary shrink-0" />
    </Link>
);

export const SectionHeader = ({ title, description }) => (
    <div className="mb-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        {description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        )}
    </div>
);

export const ConfigBranchSelect = ({ branches, value, onChange, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const currentBranch = branches.find((branch) => String(branch.id) === String(value));
    const currentLabel = currentBranch?.name || "Select branch";
    const currentIsMain = !!currentBranch?.is_main_branch;

    useEffect(() => {
        const onDocClick = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const selectBranch = (branch) => {
        onChange(String(branch.id));
        setOpen(false);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((prev) => !prev)}
                className={cn(
                    "flex items-center gap-2 px-3 h-10 rounded-full text-sm font-semibold transition-colors min-w-[200px]",
                    "border border-sidebar-primary/20 bg-sidebar-primary/15 text-sidebar-primary shadow-sm",
                    "hover:bg-sidebar-primary/20 hover:border-sidebar-primary/30",
                    open && "bg-sidebar-primary/20 border-sidebar-primary/30 ring-1 ring-sidebar-primary/10",
                    disabled && "cursor-not-allowed opacity-70"
                )}
            >
                <Building2 className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate text-left">{currentLabel}</span>
                {currentIsMain && (
                    <span className="px-1.5 py-0.5 rounded-full bg-sidebar-primary text-sidebar-primary-foreground text-[10px] font-semibold">
                        Main
                    </span>
                )}
                <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform", open && "rotate-180")} />
            </button>

                {open && !disabled && (
                    <div
                        className={cn(
                            "absolute right-0 mt-2 w-56 rounded-lg border bg-popover text-popover-foreground shadow-lg z-50",
                            "overflow-hidden animate-in fade-in-80 slide-in-from-top-2 font-sans"
                        )}
                    >
                        <div className="max-h-72 overflow-y-auto py-1">
                            {branches.map((branch) => {
                                const selected = String(branch.id) === String(value);
                                return (
                                    <button
                                        key={branch.id}
                                        type="button"
                                        onClick={() => selectBranch(branch)}
                                        className={cn(
                                            "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                                            "hover:bg-accent",
                                            selected ? "text-sidebar-primary font-semibold bg-sidebar-primary/5" : "text-foreground"
                                        )}
                                    >
                                        <Building2 className="h-4 w-4 shrink-0 opacity-70" />
                                        <span className="flex-1 truncate">
                                            {branch.name}
                                            {branch.is_main_branch && (
                                                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-sidebar-primary/15 text-sidebar-primary text-[10px] font-semibold">
                                                    Main
                                                </span>
                                            )}
                                        </span>
                                        {selected && <Check className="h-4 w-4 text-sidebar-primary shrink-0" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
        </div>
    );
};
